import React, { useEffect, useMemo, useRef, useState } from "react";
import AchievementBadge from "./AchievementBadge";
import ProfileAchievementDetailsModal from "./ProfileAchievementDetailsModal";
import styles from "./ProfileAchievementsSection.module.css";
import type {
  AchievementBadgeAnimationType,
  ProfileBorderTone,
  ProfileAchievementItemViewModel,
  UserProfileAchievementsViewModel,
} from "../types/view-models";

type ProfileAchievementsSectionProps = {
  achievements: UserProfileAchievementsViewModel | null;
  loading?: boolean;
  error?: string;
};

type ProfileAchievementsSectionBoundaryState = {
  errorMessage: string;
};

type ProfileAchievementsSectionBoundaryProps = {
  achievements: UserProfileAchievementsViewModel | null;
  children?: any;
};

const CATEGORY_ORDER: Record<string, number> = {
  trade: 0,
  bounty: 1,
  trust: 2,
};

function sortUnlocked(items: ProfileAchievementItemViewModel[]): ProfileAchievementItemViewModel[] {
  return [...items].sort((left, right) => {
    const leftCategoryOrder = CATEGORY_ORDER[String(left.category || "").toLowerCase()] ?? 99;
    const rightCategoryOrder = CATEGORY_ORDER[String(right.category || "").toLowerCase()] ?? 99;
    if (leftCategoryOrder !== rightCategoryOrder) return leftCategoryOrder - rightCategoryOrder;
    if (left.tier !== right.tier) return left.tier - right.tier;
    if (left.progress.target !== right.progress.target) return left.progress.target - right.progress.target;
    return left.name.localeCompare(right.name);
  });
}

function sortLocked(items: ProfileAchievementItemViewModel[]): ProfileAchievementItemViewModel[] {
  return [...items].sort((left, right) => {
    const leftCategoryOrder = CATEGORY_ORDER[String(left.category || "").toLowerCase()] ?? 99;
    const rightCategoryOrder = CATEGORY_ORDER[String(right.category || "").toLowerCase()] ?? 99;
    if (leftCategoryOrder !== rightCategoryOrder) return leftCategoryOrder - rightCategoryOrder;
    if (left.tier !== right.tier) return left.tier - right.tier;
    if (left.progress.target !== right.progress.target) return left.progress.target - right.progress.target;
    return left.name.localeCompare(right.name);
  });
}

function getRequirementMessages(achievement: ProfileAchievementItemViewModel): string[] {
  const messages: string[] = [];

  if (achievement.requirements.noDisputesRequired) {
    messages.push(
      achievement.requirements.noDisputesMet
        ? "No active disputes requirement met"
        : "Requires no active disputes",
    );
  }

  if (achievement.requirements.noStrikesRequired) {
    messages.push(
      achievement.requirements.noStrikesMet
        ? "No strikes requirement met"
        : "Requires no strikes",
    );
  }

  return messages;
}

function getBadgeAnimation(achievement: ProfileAchievementItemViewModel): AchievementBadgeAnimationType {
  if (!achievement.unlocked) return "NONE";
  if (achievement.tier >= 5) return "PULSE";
  if (achievement.tier >= 3) return "SHIMMER";
  return "NONE";
}

function normalizeAchievementItem(raw: any): ProfileAchievementItemViewModel | null {
  if (!raw || typeof raw !== "object") return null;

  const achievementKey = String(raw.achievementKey || raw.key || "");

  return {
    achievementId: String(raw.achievementId || raw.id || raw.achievementKey || ""),
    achievementKey,
    category: String(raw.category || "achievement"),
    name: String(raw.name || "Achievement"),
    description: String(raw.description || ""),
    tier: Number(raw.tier || 1),
    unlocked: Boolean(raw.unlocked),
    unlocksProfileBorder: Boolean(raw.unlocksProfileBorder),
    unlocksBadgeSlot: Boolean(raw.unlocksBadgeSlot),
    ...(raw.unlockedAt ? { unlockedAt: String(raw.unlockedAt) } : {}),
    progress: {
      current: Number(raw.progress?.current || 0),
      target: Number(raw.progress?.target || 0),
      percent: Number(raw.progress?.percent || 0),
      label: String(raw.progress?.label || "Progress unavailable"),
    },
    requirements: {
      noDisputesRequired: Boolean(raw.requirements?.noDisputesRequired),
      noDisputesMet: Boolean(raw.requirements?.noDisputesMet),
      noStrikesRequired: Boolean(raw.requirements?.noStrikesRequired),
      noStrikesMet: Boolean(raw.requirements?.noStrikesMet),
    },
  };
}

export function getProfileAchievementCardClassName(tone: ProfileBorderTone): string {
  if (tone === "low") return "profile-card--achievement-low";
  if (tone === "mid") return "profile-card--achievement-mid";
  if (tone === "high") return "profile-card--achievement-high";
  return "";
}

class ProfileAchievementsSectionBoundary extends React.Component<
  ProfileAchievementsSectionBoundaryProps,
  ProfileAchievementsSectionBoundaryState
> {
  constructor(props: ProfileAchievementsSectionBoundaryProps) {
    super(props);
    this.state = { errorMessage: "" };
  }

  static getDerivedStateFromError(error: Error): ProfileAchievementsSectionBoundaryState {
    return {
      errorMessage: error?.message || "Unknown achievements render error",
    };
  }

  componentDidCatch() {}

  render() {
    if (this.state.errorMessage) {
      return (
        <div className={styles.sectionState || ""}>
          Failed to render achievements: {this.state.errorMessage}
        </div>
      );
    }

    return this.props.children;
  }
}

function useIsTouchLayout(): boolean {
  const [touchLayout, setTouchLayout] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;

    const mediaQuery = window.matchMedia("(hover: none), (pointer: coarse), (max-width: 768px)");
    const sync = () => setTouchLayout(Boolean(mediaQuery.matches));
    sync();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", sync);
      return () => mediaQuery.removeEventListener("change", sync);
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(sync);
      return () => mediaQuery.removeListener(sync);
    }

    return undefined;
  }, []);

  return touchLayout;
}

function ProfileAchievementsSectionContent({
  achievements,
  loading = false,
  error = "",
}: ProfileAchievementsSectionProps) {
  const sectionRef = useRef(null as HTMLDivElement | null);
  const isTouchLayout = useIsTouchLayout();
  const [activeTooltipKey, setActiveTooltipKey] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const [selectedAchievement, setSelectedAchievement] = useState(
    null as ProfileAchievementItemViewModel | null,
  );

  const normalizedAchievements = useMemo(() => {
    if (!achievements || typeof achievements !== "object") return null;

    const unlockedAchievements = Array.isArray(achievements.unlockedAchievements)
      ? achievements.unlockedAchievements
          .map((achievement) => normalizeAchievementItem(achievement))
          .filter(Boolean) as ProfileAchievementItemViewModel[]
      : [];
    const lockedAchievements = Array.isArray(achievements.lockedAchievements)
      ? achievements.lockedAchievements
          .map((achievement) => normalizeAchievementItem(achievement))
          .filter(Boolean) as ProfileAchievementItemViewModel[]
      : [];

    return {
      ...achievements,
      unlockedAchievements,
      lockedAchievements,
      progressCounts: {
        tradeCount: Number(achievements.progressCounts?.tradeCount || 0),
        bountyFulfilledCount: Number(achievements.progressCounts?.bountyFulfilledCount || 0),
        fiveStarCount: Number(achievements.progressCounts?.fiveStarCount || 0),
      },
    };
  }, [achievements]);

  const visibleAchievements = useMemo(() => {
    if (!normalizedAchievements) return [];

    const unlocked = sortUnlocked(normalizedAchievements.unlockedAchievements || []);
    const locked = sortLocked(normalizedAchievements.lockedAchievements || []);
    return [...unlocked, ...locked];
  }, [normalizedAchievements]);

  const activeTooltipAchievement = useMemo(
    () =>
      !isTouchLayout
        ? visibleAchievements.find(
            (achievement: ProfileAchievementItemViewModel) =>
              achievement.achievementKey === activeTooltipKey,
          ) || null
        : null,
    [activeTooltipKey, isTouchLayout, visibleAchievements],
  );

  const setTooltipAnchor = (element: HTMLElement, achievementKey: string) => {
    if (isTouchLayout || !sectionRef.current) return;

    const sectionRect = sectionRef.current.getBoundingClientRect();
    const anchorRect = element.getBoundingClientRect();
    const tooltipWidth = 240;
    const preferredLeft = anchorRect.left - sectionRect.left + anchorRect.width / 2;
    const clampedLeft = Math.max(
      tooltipWidth / 2 + 12,
      Math.min(sectionRect.width - tooltipWidth / 2 - 12, preferredLeft),
    );

    setTooltipPosition({
      left: clampedLeft,
      top: anchorRect.top - sectionRect.top + 12,
    });
    setActiveTooltipKey(achievementKey);
  };

  if (loading) {
    return <div className={styles.sectionState || ""}>Loading achievements...</div>;
  }

  if (error) {
    return <div className={styles.sectionState || ""}>{error}</div>;
  }

  if (!normalizedAchievements) {
    return null;
  }

  return (
    <>
      <section ref={sectionRef} className={styles.section || ""} aria-label="Achievements">
        <div className={styles.header || ""}>
          <div>
            <h3 className={styles.title || ""}>Achievements</h3>
            <p className={styles.subtitle || ""}>
              {normalizedAchievements.unlockedAchievements.length} unlocked • {normalizedAchievements.lockedAchievements.length} still to earn
            </p>
          </div>
          <div className={styles.summaryMetrics || ""}>
            <span className={styles.metricChip || ""}>Trades {normalizedAchievements.progressCounts.tradeCount}</span>
            <span className={styles.metricChip || ""}>Bounties {normalizedAchievements.progressCounts.bountyFulfilledCount}</span>
            <span className={styles.metricChip || ""}>5★ {normalizedAchievements.progressCounts.fiveStarCount}</span>
          </div>
        </div>

        <div className={styles.badgeShelfViewport || ""}>
          <div className={styles.badgeShelf || ""} data-testid="achievement-scroll-strip">
          {visibleAchievements.map((achievement: ProfileAchievementItemViewModel) => {
            const tooltipId = `achievement-tooltip-${achievement.achievementKey}`;
            const tooltipVisible =
              !isTouchLayout && activeTooltipKey === achievement.achievementKey;

            return (
              <div
                key={achievement.achievementKey}
                className={styles.badgeWrap || ""}
                onMouseEnter={(event: any) =>
                  setTooltipAnchor(event.currentTarget as HTMLDivElement, achievement.achievementKey)
                }
                onMouseLeave={() => !isTouchLayout && setActiveTooltipKey("")}
              >
                <button
                  type="button"
                  className={styles.badgeButton || ""}
                  aria-describedby={tooltipVisible ? tooltipId : undefined}
                  onFocus={(event: any) =>
                    setTooltipAnchor(event.currentTarget as HTMLButtonElement, achievement.achievementKey)
                  }
                  onBlur={() => !isTouchLayout && setActiveTooltipKey("")}
                  onClick={() => {
                    if (isTouchLayout) {
                      setSelectedAchievement(achievement);
                    }
                  }}
                >
                  <AchievementBadge
                    badge={{
                      id: achievement.achievementId,
                      key: achievement.achievementKey,
                      name: achievement.name,
                      label: achievement.name,
                      category: achievement.category,
                    }}
                    unlocked={achievement.unlocked}
                    tier={achievement.tier}
                    animationType={getBadgeAnimation(achievement)}
                  />
                </button>
              </div>
            );
          })}
          </div>
        </div>

        {activeTooltipAchievement ? (
          <div
            id={`achievement-tooltip-${activeTooltipAchievement.achievementKey}`}
            role="tooltip"
            className={styles.tooltip || ""}
            style={{
              left: `${tooltipPosition.left}px`,
              top: `${tooltipPosition.top}px`,
            }}
          >
            <div className={styles.tooltipTitle || ""}>
              {activeTooltipAchievement.name}
            </div>
            <div className={styles.tooltipDescription || ""}>
              {activeTooltipAchievement.description}
            </div>
            <div className={styles.tooltipProgress || ""}>
              {activeTooltipAchievement.progress.label}
            </div>
            {getRequirementMessages(activeTooltipAchievement).map((message) => (
              <div key={message} className={styles.tooltipRequirement || ""}>
                {message}
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <ProfileAchievementDetailsModal achievement={selectedAchievement} onClose={() => setSelectedAchievement(null)} />
    </>
  );
}

export default function ProfileAchievementsSection(props: ProfileAchievementsSectionProps) {
  return (
    <ProfileAchievementsSectionBoundary achievements={props.achievements}>
      <ProfileAchievementsSectionContent {...props} />
    </ProfileAchievementsSectionBoundary>
  );
}
